import { TranslationsPanel } from '@/components/TranslationsPanel';
import { TranslationWork } from '@/components/TranslationWork';
import { useQueryClient } from '@tanstack/react-query';
import { applyToolEffects } from '@/lib/mediaEffects';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef, useMemo } from 'react';
import { usePage, useUpdatePage, PageEditorMode, pagesApi, type Page } from '@wordpuppi/shared';
import { useBaxterChanged } from '@/lib/baxterEffects';
import { BaxterChangedBar } from '@/components/baxter/BaxterChangedBar';
import { openPagePreview } from '@/lib/pagePreview';
import { customTemplate, designPage } from '@/lib/designPage';
import { PageTemplateSelect } from '@/components/PageTemplateSelect';
import { UnsavedGuard } from '@/components/UnsavedGuard';
import { TiptapEditor } from '@/components/TiptapEditor';
import { TeraEditor } from '@/components/TeraEditor';
import { GeoPanel } from '@/components/GeoPanel';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SaveSplitButton } from '@/components/SaveSplitButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ChevronDown } from 'lucide-react';

export const Route = createFileRoute('/sites/$siteSlug/pages/$pageId')({
  component: EditPagePage,
});

function EditPagePage() {
  const { siteSlug, pageId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = usePage(siteSlug, pageId);
  const updatePage = useUpdatePage(siteSlug);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [slug, setSlug] = useState('');
  const [editorMode, setEditorMode] = useState<PageEditorMode>('richtext');
  const [showTitle, setShowTitle] = useState(true);
  const [showInHeader, setShowInHeader] = useState(true);
  const [showInFooter, setShowInFooter] = useState(true);
  const [customCss, setCustomCss] = useState('');
  const [customJs, setCustomJs] = useState('');
  const [template, setTemplate] = useState<string | null>(null);
  const [designing, setDesigning] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Track if form has been initialized from server data
  // This prevents refetches from overwriting user edits
  const formInitialized = useRef(false);

  // The rich-text editor reformats its content on mount, firing onChange with its own
  // serialization before any user edit. We baseline the dirty check on that FIRST settled
  // emission, not the stored string, so a pristine load reads clean. (Same fix as posts.)
  const [settledContent, setSettledContent] = useState<string | null>(null);
  useEffect(() => {
    setSettledContent(null);
  }, [pageId]);
  const handleContentChange = (value: string) => {
    setContent(value);
    if (formInitialized.current && settledContent === null) {
      setSettledContent(value);
    }
  };

  // Server data -> form. Shared by the initial load and by a Baxter reload, so
  // "put what's on the server into the fields" has exactly one definition.
  const applyServer = (p: Page) => {
    setTitle(p.title);
    setContent(p.content || '');
    setStatus(p.status);
    setSlug(p.slug);
    setEditorMode(p.editor_mode || 'richtext');
    setShowTitle(p.show_title ?? true);
    setShowInHeader(p.show_in_header ?? true);
    setShowInFooter(p.show_in_footer ?? true);
    setCustomCss(p.custom_css || '');
    setCustomJs(p.custom_js || '');
    setTemplate(customTemplate(p.template));
    // Auto-expand if there's existing custom code
    if (p.custom_css || p.custom_js) {
      setAdvancedOpen(true);
    }
  };

  // Load page data into form (only on initial load)
  useEffect(() => {
    if (data?.data && !formInitialized.current) {
      applyServer(data.data);
      formInitialized.current = true;
    }
  }, [data]);

  // AB#675: Baxter edited THIS page. A clean editor just re-reads (the panel has
  // already invalidated the query, so `refetch` is usually a cache hit); a dirty
  // one keeps every keystroke and offers the bar instead.
  const baxterAt = useBaxterChanged('pages', pageId);
  const seenBaxter = useRef<number | null>(null);
  const [changedBar, setChangedBar] = useState(false);
  const reloadFromServer = async () => {
    const fresh = await refetch();
    const p = fresh.data?.data;
    if (!p) return;
    applyServer(p);
    setSettledContent(null); // re-baseline on what the server now holds
    setChangedBar(false);
  };

  // AB#430: dirty tracking against the server-loaded values; resets after a save refetch.
  const baseline = useMemo(() => {
    if (!data?.data) return null;
    return JSON.stringify({
      title: data.data.title,
      // settledContent is the editor's own serialization of the loaded doc; fall back to
      // the stored string until the editor settles (e.g. before first onChange fires).
      content: settledContent ?? (data.data.content || ''),
      status: data.data.status,
      slug: data.data.slug,
      show_title: data.data.show_title ?? true,
      show_in_header: data.data.show_in_header ?? true,
      show_in_footer: data.data.show_in_footer ?? true,
      custom_css: data.data.custom_css || '',
      custom_js: data.data.custom_js || '',
      template: customTemplate(data.data.template),
    });
  }, [data, settledContent]);
  const current = JSON.stringify({
    title,
    content,
    status,
    slug,
    show_title: showTitle,
    show_in_header: showInHeader,
    show_in_footer: showInFooter,
    custom_css: customCss,
    custom_js: customJs,
    template,
  });
  const dirty = baseline !== null && baseline !== current;

  useEffect(() => {
    if (baxterAt == null || baxterAt === seenBaxter.current) return;
    seenBaxter.current = baxterAt;
    if (dirty) setChangedBar(true);
    else void reloadFromServer();
  }, [baxterAt, dirty]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error || !data) {
    return <div className="text-center py-12 text-muted-foreground">Page not found</div>;
  }

  const isTeraMode = editorMode === 'tera';
  // Legacy canvas page (pre-AB#945): its body belongs to the page designer, so
  // only metadata is editable here — content/editor_mode/css are never sent.
  const isDesigned = data.data?.editor_mode === 'designed';

  const doSave = async () => {
    try {
      await updatePage.mutateAsync({
        id: pageId,
        data: isDesigned
          ? { title, status, slug, show_title: showTitle, show_in_header: showInHeader, show_in_footer: showInFooter }
          : {
              title,
              content,
              status,
              slug,
              show_title: showTitle,
              show_in_header: showInHeader,
              show_in_footer: showInFooter,
              custom_css: customCss || undefined,
              custom_js: customJs || undefined,
              template,
            },
      });
      // Re-baseline on what was just persisted — the mount-time snapshot is stale
      // now (same serializer delta that caused the false-dirty on load).
      setSettledContent(content);
      void applyToolEffects(queryClient, siteSlug, { name: 'wpp_update_content', args: { site_slug: siteSlug, content_type: 'pages', id: pageId, data: { title, content, custom_css: customCss, custom_js: customJs } }, previousData: data.data }, { ok: true }).catch(() => {});
      toast.success('Page updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update page');
      throw err;
    }
  };

  const handleSave = async () => {
    await doSave();
  };

  const handleSaveAndClose = async () => {
    await doSave();
    navigate({ to: '/sites/$siteSlug/pages', params: { siteSlug } });
  };

  const handlePreview = () =>
    openPagePreview(siteSlug, {
      title,
      content,
      slug,
      editor_mode: editorMode,
      custom_css: customCss,
      custom_js: customJs,
      show_title: showTitle,
      template,
    });

  /** AB#945: the page gets (or opens) its own theme template in the template designer. */
  const handleDesign = async () => {
    setDesigning(true);
    try {
      const target = await designPage(siteSlug, data.data!);
      if (!target) return;
      // designPage already saved the template; mirror it so the refetch below
      // doesn't read as an unsaved edit and trip UnsavedGuard on the way out.
      setTemplate(customTemplate(target.search.path.replace(/^templates\//, '')));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sites', siteSlug] }),
        queryClient.invalidateQueries({ queryKey: ['account', 'themes'] }),
      ]);
      await navigate(target);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to open the page design');
    } finally {
      setDesigning(false);
    }
  };
  const savedTemplate = customTemplate(data.data?.template);

  return (
    <div>
      <UnsavedGuard dirty={dirty} noun="page" />
      {changedBar && (
        <BaxterChangedBar
          noun="page"
          onReload={() => void reloadFromServer()}
          onDismiss={() => setChangedBar(false)}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
        <h1 className="text-2xl font-semibold">Edit Page</h1>
        {isDesigned ? (
          <Button variant="outline" asChild><Link to="/sites/$siteSlug/pages/$pageId/design" params={{ siteSlug, pageId }}>Open in designer</Link></Button>
        ) : !isTeraMode && (
          <Button variant="outline" disabled={designing} onClick={handleDesign}>{savedTemplate ? 'Edit design' : 'Design page'}</Button>
        )}
        <Button variant="secondary" onClick={handlePreview}>
          Preview
        </Button>
      </div>

      {/* Editor mode badge (read-only) */}
      <Badge variant="secondary" className="mb-4">
        Mode: {isTeraMode ? 'Tera Template' : isDesigned ? 'Canvas design' : 'Rich Text'}
      </Badge>

      <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="page-url-slug"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            id="showTitle"
            type="checkbox"
            checked={showTitle}
            onChange={(e) => setShowTitle(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          <Label htmlFor="showTitle">Show title on page</Label>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="showInHeader"
            type="checkbox"
            checked={showInHeader}
            onChange={(e) => setShowInHeader(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          <Label htmlFor="showInHeader">Show in header navigation</Label>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="showInFooter"
            type="checkbox"
            checked={showInFooter}
            onChange={(e) => setShowInFooter(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          <Label htmlFor="showInFooter">Show in footer navigation</Label>
        </div>

        {!isDesigned && <PageTemplateSelect siteSlug={siteSlug} value={template} onChange={setTemplate} />}

        {(isDesigned || savedTemplate) && (
          <div className="space-y-2">
            <Label>Design preview</Label>
            <PagePreviewFrame siteSlug={siteSlug} page={data.data!} />
            <p className="text-sm text-muted-foreground">
              {isDesigned
                ? 'This page was laid out in the canvas designer — open it there to change its body.'
                : `The ${savedTemplate} template wraps the content below. Use Edit design to change the layout.`}
            </p>
          </div>
        )}

        {!isDesigned && <div className="space-y-2">
          <Label>Content</Label>
          {isTeraMode ? (
            <TeraEditor content={content} onChange={handleContentChange} siteSlug={siteSlug} />
          ) : (
            <TiptapEditor content={content} onChange={handleContentChange} siteSlug={siteSlug} folder="pages" />
          )}
        </div>}

        {/* Advanced: Custom CSS/JS — a designed page's CSS is the canvas's own. */}
        {!isDesigned && <div className="space-y-4">
          <button
            type="button"
            onClick={() => setAdvancedOpen(!advancedOpen)}
            className="flex items-center gap-2 font-medium text-sm hover:text-foreground/80"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ease-out ${advancedOpen ? 'rotate-180' : ''}`} />
            Advanced Options
          </button>
          {advancedOpen && (
            <div className="disclosure-reveal space-y-4 pl-6 border-l-2 border-muted">
              <div className="space-y-2">
                <Label htmlFor="customCss">Custom CSS</Label>
                <Textarea
                  id="customCss"
                  value={customCss}
                  onChange={(e) => setCustomCss(e.target.value)}
                  placeholder="/* CSS styles for this page only */"
                  className="font-mono text-sm min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground">
                  CSS will be injected into the page's &lt;head&gt; tag.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customJs">Custom JavaScript</Label>
                <Textarea
                  id="customJs"
                  value={customJs}
                  onChange={(e) => setCustomJs(e.target.value)}
                  placeholder="// JavaScript for this page only"
                  className="font-mono text-sm min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground">
                  JavaScript will be injected before the closing &lt;/body&gt; tag.
                </p>
              </div>
            </div>
          )}
        </div>}

        {/* Pages have no excerpt/author/date fields, so GEO scores content only.
            Skip in Tera mode (raw template, not article prose). */}
        {!isTeraMode && !isDesigned && <GeoPanel content={content} />}

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select value={status} onValueChange={(v: string) => setStatus(v as 'draft' | 'published')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2 pt-4">
          <SaveSplitButton
            onSave={handleSave}
            onSaveAndClose={handleSaveAndClose}
            isPending={updatePage.isPending}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate({ to: '/sites/$siteSlug/pages', params: { siteSlug } })}
          >
            Cancel
          </Button>
        </div>
      </form>
      {!isDesigned && <TranslationsPanel siteSlug={siteSlug} kind="page" sourceId={pageId} />}
      {!isDesigned && <TranslationWork siteSlug={siteSlug} kind="page" sourceId={pageId} />}
    </div>
  );
}

/**
 * Read-only render of the SAVED page through its template (preview-full, the
 * same render the Preview button opens) — re-renders after each save.
 */
function PagePreviewFrame({ siteSlug, page }: { siteSlug: string; page: Page }) {
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    pagesApi
      .previewFull(siteSlug, {
        title: page.title,
        content: page.content || '',
        slug: page.slug || 'preview',
        editor_mode: page.editor_mode,
        custom_css: page.custom_css || undefined,
        custom_js: page.custom_js || undefined,
        show_title: page.show_title ?? true,
        template: page.template ?? null,
        render_only: true,
      })
      .then((r) => {
        if (!live) return;
        if (r.data && 'html' in r.data) { setHtml(r.data.html); setFailed(null); }
        else setFailed(r.data && 'error' in r.data ? r.data.error : 'The preview did not render.');
      })
      .catch((err) => { if (live) setFailed(err instanceof Error ? err.message : 'The preview did not render.'); });
    return () => { live = false; };
  }, [siteSlug, page]);
  if (failed) return <p role="alert" className="text-sm text-destructive">{failed}</p>;
  if (html == null) return <p className="text-sm text-muted-foreground">Rendering preview…</p>;
  return <iframe title="Page design preview" srcDoc={html} sandbox="allow-same-origin" className="h-[480px] w-full rounded-md border bg-white" />;
}
